import { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * Dashboard bus — small shared state so independent widgets can coordinate
 * without prop drilling. Currently exposes:
 *   - taskRefreshKey  : integer bumped whenever a widget creates/updates a
 *                       task; sibling widgets subscribe and refetch.
 *   - bumpTaskRefresh : bump the key.
 *
 * Optional pattern — widgets can safely call `useDashboardBus()` even when
 * they're rendered outside the provider (falls back to a stable no-op bus).
 */
const NOOP_BUS = { taskRefreshKey: 0, bumpTaskRefresh: () => {} };

const DashboardBusContext = createContext(NOOP_BUS);

export function DashboardBusProvider({ children }) {
  const [taskRefreshKey, setTaskRefreshKey] = useState(0);
  const bumpTaskRefresh = useCallback(() => setTaskRefreshKey((k) => k + 1), []);
  const value = useMemo(() => ({ taskRefreshKey, bumpTaskRefresh }), [taskRefreshKey, bumpTaskRefresh]);
  return (
    <DashboardBusContext.Provider value={value}>
      {children}
    </DashboardBusContext.Provider>
  );
}

export function useDashboardBus() {
  return useContext(DashboardBusContext);
}
